/**
 * @fileoverview Mutation hook for the Invite HR Admin command.
 * @description Sends the invitation through `useIdempotentMutation`, which keeps the command's
 * `Idempotency-Key` in a registry on the QueryClient: a retry after a timeout or a 5xx, even from a
 * dialog that was closed and reopened, replays the first request instead of inviting twice. The key
 * is scoped to the client and the invitee's details.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useIdempotentMutation } from '@/features/sales/hooks/useIdempotentMutation'
import { inviteClientHrAdmin } from '@/features/sales/services/sales-clients.service'

import type {
  HrAdminInvited,
  InviteHrAdminRequest,
} from '@/features/sales/types/sales-client.types'

/** The input of one invite command. */
export interface InviteClientHrAdminInput {
  tenantId: string
  request: InviteHrAdminRequest
}

/**
 * @description Invites a client's HR Admin under a kept key, then refreshes every sales query so the
 * details, grid and KPI tiles show the new state.
 * @param {(result: HrAdminInvited) => void} [onSuccess] - Called after a success, even when the
 * dialog has unmounted.
 * @returns {ReturnType<typeof useIdempotentMutation>} The mutation (mutate with the client and the
 * invitee).
 */
export function useInviteClientHrAdmin(onSuccess?: (result: HrAdminInvited) => void) {
  return useIdempotentMutation<InviteClientHrAdminInput, HrAdminInvited>({
    action: 'invite-client-hr-admin',
    /**
     * @description Sends the invitation under the key it is given.
     * @param {InviteClientHrAdminInput} input - The client and the invitee.
     * @param {string} key - The Idempotency-Key.
     * @returns {Promise<HrAdminInvited>} The invitation sent.
     */
    send: ({ tenantId, request }, key) => inviteClientHrAdmin(tenantId, request, key),
    onSuccess,
  })
}
